import type { TocItem } from '#features/blog/components/TableOfContents';

const entities: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  copy: '©',
  reg: '®',
  trade: '™',
  bull: '•',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  middot: '·',
};

/** Plain heading labels, computed identically on the server and in the browser. */
export const headingText = (html: string): string =>
  html
    .replaceAll(/<br\s*\/?>/gi, ' ')
    .replaceAll(tokens, '')
    .replaceAll(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (source, entity: string) => {
      if (!entity.startsWith('#')) return entities[entity.toLowerCase()] ?? source;
      const value = entity.toLowerCase().startsWith('#x')
        ? Number.parseInt(entity.slice(2), 16)
        : Number.parseInt(entity.slice(1), 10);
      return value > 0 && value <= 0x10ffff && !(value >= 0xd800 && value <= 0xdfff)
        ? String.fromCodePoint(value)
        : source;
    })
    .replaceAll(/\s+/g, ' ')
    .trim();

export const headingSlug = (text: string): string =>
  text
    .toLowerCase()
    .replaceAll(/[^a-zа-яё0-9\s-]/gi, '')
    .replaceAll(/\s+/g, '-')
    .replaceAll(/-+/g, '-')
    .slice(0, 60);

const idAttribute = /(?:^|\s)id\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i;
const tokens = /<!--[\s\S]*?-->|<\/?[a-zA-Z][\w:-]*\b(?:[^"'<>]|"[^"]*"|'[^']*')*>/g;

/**
 * The CMS already sanitizes HTML. This is display preparation, not a sanitizer: only heading
 * anchors and list marker hints are added, without rewriting paragraphs, media or the saved DTO.
 * No DOM APIs: SSR sends working anchor destinations before hydration.
 */
export const prepareLegacyBlogContent = (html: string): { html: string; tocItems: TocItem[] } => {
  const matches = [...html.matchAll(tokens)];
  const ids = new Map<string, number>();
  for (const match of matches) {
    if (match[0].startsWith('<!--')) continue;
    const attribute = match[0].match(idAttribute);
    const id = attribute?.[1] ?? attribute?.[2] ?? attribute?.[3];
    if (id) ids.set(id, (ids.get(id) ?? 0) + 1);
  }
  const used = new Set(ids.keys());
  const edits: { start: number; end: number; text: string }[] = [];
  const tocItems: TocItem[] = [];
  let excludedDepth = 0;
  let heading: { start: number; end: number; tag: string; opening: string } | null = null;

  for (const match of matches) {
    const token = match[0];
    if (token.startsWith('<!--')) continue;
    const tag = token.match(/^<\/?([\w:-]+)/)?.[1].toLowerCase();
    const closing = token.startsWith('</');
    if (tag && ['pre', 'code', 'script', 'style', 'textarea'].includes(tag)) {
      excludedDepth = Math.max(0, excludedDepth + (closing ? -1 : 1));
      continue;
    }
    if (excludedDepth || !tag) continue;
    const start = match.index;
    const end = start + token.length;
    if (!closing && (tag === 'h2' || tag === 'h3')) {
      heading = { start, end, tag, opening: token };
    } else if (closing && heading?.tag === tag) {
      const text = headingText(html.slice(heading.end, start));
      if (text) {
        const attribute = heading.opening.match(idAttribute);
        const oldId = attribute?.[1] ?? attribute?.[2] ?? attribute?.[3];
        let id = oldId && ids.get(oldId) === 1 && /^[\w\p{L}-]+$/u.test(oldId) ? oldId : '';
        if (!id) {
          const base = `heading-${tocItems.length}-${headingSlug(text)}`;
          id = base;
          let suffix = 1;
          while (used.has(id)) id = `${base}-${suffix++}`;
          const opening = heading.opening.replace(idAttribute, '');
          edits.push({
            start: heading.start,
            end: heading.end,
            text: opening.replace(/>$/, ` id="${id}">`),
          });
        }
        used.add(id);
        tocItems.push({ id, text, level: Number(tag[1]) });
      }
      heading = null;
    }
    if (!closing && tag === 'li' && /\bdata-list\s*=/.test(token)) {
      const body = html.slice(end).split(/<\/li\s*>/i)[0];
      if (!/^\s*<span\b[^>]*\bclass=(?:"[^"]*\bql-ui\b[^"]*"|'[^']*\bql-ui\b[^']*')/i.test(body)) {
        edits.push({ start: end, end, text: '<span class="ql-ui" aria-hidden="true"></span>' });
      }
    }
    if (!closing && tag === 'li' && !/\b(?:data-list|data-blog-prefixed)\s*=/.test(token)) {
      const after = html.slice(
        end,
        html.indexOf('</li', end) < 0 ? undefined : html.indexOf('</li', end)
      );
      const text = headingText(after.split(/<(?:ul|ol)\b/i)[0]);
      if (/^(?:[•●▪·]\s*|\d+[.)]\s+)/.test(text)) {
        edits.push({ start, end, text: token.replace(/>$/, ' data-blog-prefixed="true">') });
        // The common legacy form stores the marker as leading text. Give it its own gutter
        // at display time, retaining its exact value and leaving the saved article unchanged.
        const prefix = /\bql-indent-\d\b/.test(token)
          ? null
          : after.match(/^(\s*)([•●▪·]|\d+[.)])\s+/);
        if (prefix) {
          const prefixStart = end + prefix[1].length;
          edits.push({
            start: prefixStart,
            end: end + prefix[0].length,
            text: `<span class="blog-list-marker">${prefix[2]}</span> `,
          });
        }
      }
    }
  }
  let output = html;
  for (const edit of edits.toSorted((a, b) => b.start - a.start)) {
    output = output.slice(0, edit.start) + edit.text + output.slice(edit.end);
  }
  return { html: output, tocItems };
};
